import { FileReferencesSection } from "@/components/features/matter/file-repo/FileReferencesSection";
import { LinkedMatterMembershipPopover } from "@/components/features/matter/shared/LinkedMatterMembershipPopover";
import { RelatedMatterObjectDeleteModal } from "@/components/features/matter/shared/RelatedMatterObjectDeleteModal";
import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  Separator,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { TimeFormField } from "@/components/ui/FormFields/TimeFormField/TimeFormField";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useSyncIncidentReportContactConnections } from "@/lib/data/react-query/connections/incident-report-contact-connection";
import {
  useCreateMatterIncidentReport,
  useUpdateMatterIncidentReport,
} from "@/lib/data/react-query/matters/matter-incident-report";
import {
  useLinkObjectToRelatedMatters,
  useUnlinkObjectFromMatter,
} from "@/lib/data/react-query/matters/matter-related-object";
import { ContactConnectionInFormSchema } from "@/lib/data/schemas/contact/ContactSchema";
import { MatterIncidentReportDataSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { queryKeys } from "@/lib/data/static/query-keys";
import { MATTER_INCIDENT_REPORT_SENTIMENT_OPTIONS } from "@/lib/data/static/fileStatics";
import { MATTER_INCIDENT_REPORT_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type {
  APIResponse,
  Connection,
  Contact,
  MatterContactConnection,
  MatterIncidentReport,
  MatterIncidentReportData,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { mergeFilesAndReferenceId } from "@/lib/utils/matterFile";
import {
  createIncidentReportContactConnectionChildInput,
  getIncidentReportConnectionFormContact,
  getIncidentReportConnectionPreviewContact,
  getIncidentReportContactConnections,
} from "@/lib/utils/connections/incident-report-contact-connections";
import { modalFormFieldsetClassName } from "@/lib/utils/modal-classes";
import { getRelatedMatterSelectionOptions } from "@/lib/utils/relatedMatters";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { isNil, omitBy } from "lodash";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { type z } from "zod";

interface AddOrEditIncidentReportModalProps {
  title: string;
  description?: string;
  editing: boolean;
  matterId: string;
  incidentReportId?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  initialData?: Partial<MatterIncidentReport>;
}

function incidentReportSourceMatterContactRolePreview(
  contact: Contact,
  matterId: string,
  storedConnection: Connection | undefined,
  onRemoveFromForm?: () => void,
) {
  const previewContact =
    getIncidentReportConnectionPreviewContact(storedConnection) ?? contact;
  return (
    <MatterContactConnectionModalPreview
      contactId={contact.id}
      previewContact={previewContact}
      fetchContactOnEdit={Boolean(storedConnection)}
      matterId={matterId}
      matterContactConnection={
        {
          label: "Incident report source",
          contactId: contact.id,
          contact: previewContact,
        } as MatterContactConnection
      }
      onRemoveContact={
        onRemoveFromForm
          ? () => {
              onRemoveFromForm();
            }
          : undefined
      }
    />
  );
}

const incidentReportFormSchema = MatterIncidentReportDataSchema.pick({
  status: true,
  description: true,
  fileNumber: true,
  sentiment: true,
  time: true,
}).extend({
  incidentReportSourceContacts: ContactConnectionInFormSchema.array(),
});

const defaultFormValues: z.infer<typeof incidentReportFormSchema> = {
  status: MATTER_INCIDENT_REPORT_STATUS_OPTIONS[0],
  description: "" as const,
  fileNumber: "",
  incidentReportSourceContacts: [],
  sentiment: "",
  time: "",
};

type IncidentReportFormValues = z.infer<typeof incidentReportFormSchema>;

const getSelectedContactId = (
  contact:
    | IncidentReportFormValues["incidentReportSourceContacts"][number]
    | undefined,
) => {
  const id = contact?.id ?? contact?.contactId;
  return typeof id === "string" ? id : undefined;
};

const getIncidentReportSourceFormValues = (
  connections: Connection[],
): IncidentReportFormValues["incidentReportSourceContacts"] =>
  connections.flatMap((connection) => {
    const contact = getIncidentReportConnectionFormContact(connection);
    return contact ? [contact] : [];
  });

const INCIDENT_REPORT_MODAL_FORM_ID = "add-incident-report-modal-form";

export const AddOrEditIncidentReportModal = ({
  title,
  description,
  editing,
  matterId,
  incidentReportId,
  open,
  setOpen,
  initialData,
}: AddOrEditIncidentReportModalProps) => {
  const { hasActionPermission } = useUser();
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const queryClient = useQueryClient();
  const matter = matterData.data;
  const incidentReport = matter.incidentReports.find(
    (report) => report.id === incidentReportId,
  );
  const incidentReportInitialValues = mergeInitialData(
    incidentReport,
    initialData,
  );
  const incidentReportSourceConnections = getIncidentReportContactConnections(
    incidentReport,
    "Incident Report Source",
  );

  const fileReferences = mergeFilesAndReferenceId(
    matter,
    "incidentReportId",
    incidentReportInitialValues?.id,
  );

  const incidentReportForm = useForm<IncidentReportFormValues>({
    resolver: zodResolver(incidentReportFormSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...(incidentReportInitialValues ?? {}),
          fileNumber: incidentReportInitialValues?.fileNumber ?? "",
          incidentReportSourceContacts: getIncidentReportSourceFormValues(
            incidentReportSourceConnections,
          ),
          time: incidentReportInitialValues?.time ?? "",
        }
      : defaultFormValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const { updateSlideoverPayload } = useModals();
  const createIncidentReport = useCreateMatterIncidentReport({
    showSuccessToast: false,
  });
  const updateIncidentReport = useUpdateMatterIncidentReport({
    showSuccessToast: false,
  });
  const unlinkIncidentReport = useUnlinkObjectFromMatter({
    objectLabel: "Incident report",
  });
  const syncIncidentReportConnections =
    useSyncIncidentReportContactConnections();
  const linkObjectToRelatedMatters = useLinkObjectToRelatedMatters();

  const [isDeleting, setIsDeleting] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const relatedMatterOptions = useMemo(
    () => getRelatedMatterSelectionOptions(matter),
    [matter],
  );
  /** Covers incident report mutation + chained contact-connection work (RQ `isPending` drops between calls). */
  const [isIncidentReportSaving, setIsIncidentReportSaving] = useState(false);
  /** Prevents overlapping submits before React re-disables footer buttons (`loading`). */
  const incidentReportSubmitLockRef = useRef(false);

  const isFormLoading = isIncidentReportSaving;

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIncidentReport,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIncidentReport,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterIncidentReport,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canCopyToRelatedMatters =
    !editing && relatedMatterOptions.length > 0 && canCreate;

  const onSubmit = async (
    data: IncidentReportFormValues,
    relatedMatterIds: string[] = [],
    shouldCloseAfterSave = closeAfterSave,
  ) => {
    if (incidentReportSubmitLockRef.current) {
      return;
    }
    incidentReportSubmitLockRef.current = true;
    setIsIncidentReportSaving(true);
    let resData: APIResponse<MatterIncidentReportData>;
    let allRelatedMatterLinksSucceeded = true;
    try {
      const selectedSourceIds = [
        ...new Set(
          data.incidentReportSourceContacts
            .map(getSelectedContactId)
            .filter((id): id is string => Boolean(id)),
        ),
      ];
      if (editing && incidentReport) {
        // edit submit here
        const result = await updateIncidentReport.mutateAsync({
          ...incidentReport,
          ...data,
        });
        resData = result;
        await syncIncidentReportConnections.mutateAsync({
          currentIncidentReport: incidentReport,
          desiredContactIdsByRole: {
            "Incident Report Source": selectedSourceIds,
          },
          incidentReportId: result.data.id,
          matterId: matter.id,
        });
      } else {
        const result = await createIncidentReport.mutateAsync({
          ...data,
          matterId: matter.id,
          connections: selectedSourceIds.map((contactId) =>
            createIncidentReportContactConnectionChildInput(
              contactId,
              "Incident Report Source",
            ),
          ),
        });
        resData = result;
      }
      if (relatedMatterIds.length > 0) {
        const { failures } = await linkObjectToRelatedMatters.mutateAsync({
          objectType: "incident-report",
          objectId: resData.data.id,
          sourceMatterId: matter.id,
          matterIds: relatedMatterIds,
        });
        allRelatedMatterLinksSucceeded = failures.length === 0;
      }
      await queryClient.refetchQueries({
        queryKey: [queryKeys.matters, matter.id],
      });
      if (shouldCloseAfterSave && allRelatedMatterLinksSucceeded) {
        setOpen(false);
        incidentReportForm.reset({
          ...defaultFormValues,
          ...omitBy(resData.data, isNil),
          incidentReportSourceContacts: data.incidentReportSourceContacts,
        });
      } else {
        updateSlideoverPayload("incident-report", {
          editing: true,
          incidentReportId: resData.data.id,
          title: SLIDEOVER_TITLE.INCIDENT_REPORT.UPDATE,
          description: SLIDEOVER_DESCRIPTION.INCIDENT_REPORT.UPDATE,
        });
      }

      if (!editing) {
        toast.success("Incident report created successfully");
      } else {
        toast.success("Incident report updated successfully");
      }
    } finally {
      incidentReportSubmitLockRef.current = false;
      setIsIncidentReportSaving(false);
    }
  };

  useEffect(() => {
    if (!open) {
      incidentReportForm.reset(defaultFormValues);
      setCloseAfterSave(false);
      incidentReportSubmitLockRef.current = false;
      setIsIncidentReportSaving(false);
    }
  }, [open, incidentReportForm]);

  const submitIncidentReportForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (incidentReportSubmitLockRef.current) {
      return;
    }
    await incidentReportForm.handleSubmit((data) => onSubmit(data))();
  };

  const fieldsetDisabled = editing ? !canUpdate : !canCreate;

  const incidentReportModalContactPickerProps = {
    contactPickerPresentation: "modal" as const,
    contactSelectorSize: "compact" as const,
    hideCompactContactChrome: true,
  };

  const incidentReportMainFieldsFieldset = (
    <fieldset
      className={cn(modalFormFieldsetClassName)}
      disabled={fieldsetDisabled}
    >
      <SimpleSelectFormField
        register={incidentReportForm.register("status")}
        items={MATTER_INCIDENT_REPORT_STATUS_OPTIONS}
        placeholder="Select status"
        label="Status"
      />
      <SimpleSelectFormField
        register={incidentReportForm.register("sentiment")}
        items={MATTER_INCIDENT_REPORT_SENTIMENT_OPTIONS}
        placeholder="Select sentiment"
        label="Sentiment"
        clearable
      />
      <TimeFormField
        name="time"
        register={incidentReportForm.register("time")}
        label="Time"
      />
      <TextFormField
        register={incidentReportForm.register("fileNumber")}
        label="File number"
      />
      <div className="col-span-full sm:col-span-2">
        <TextAreaFormField
          register={incidentReportForm.register("description")}
          label="Report description"
        />
      </div>
      <div className="col-span-full mt-6 grid gap-y-2">
        <Separator />
        <FileReferencesSection
          editing={editing}
          fileReferences={fileReferences}
          matter={matter}
          itemKey="incidentReportId"
          itemId={incidentReport?.id ?? ""}
          permissionKey={ACTION_PERMISSION_KEYS.matterIncidentReport}
        />
      </div>
    </fieldset>
  );

  const modalFooter = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        {editing && incidentReport && canDelete && (
          <Button
            type="button"
            variant="destructive"
            disabled={unlinkIncidentReport.isPending}
            onClick={() => {
              setIsDeleting(true);
            }}
          >
            Delete
          </Button>
        )}
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <FormCancelButton
          setOpen={setOpen}
          isDirty={incidentReportForm.formState.isDirty}
        />
        <FormSubmitButton
          variant="light-primary"
          loading={isFormLoading}
          overrideDisabled={Boolean(initialData)}
          submitFn={() => {
            setCloseAfterSave(false);
          }}
          submitFormId={INCIDENT_REPORT_MODAL_FORM_ID}
        />
        <FormSubmitButton
          variant="default"
          loading={isFormLoading}
          overrideDisabled={Boolean(initialData)}
          type={canCopyToRelatedMatters ? "button" : "submit"}
          submitFormId={INCIDENT_REPORT_MODAL_FORM_ID}
          additionalActions={
            canCopyToRelatedMatters
              ? [
                  {
                    label: "Save to this matter only",
                    onSelect: () => {
                      void incidentReportForm.handleSubmit((data) =>
                        onSubmit(data, [], true),
                      )();
                    },
                  },
                ]
              : undefined
          }
          title={
            canCopyToRelatedMatters
              ? "Save to this & related matters"
              : "Save and close"
          }
          submitFn={() => {
            if (canCopyToRelatedMatters) {
              void incidentReportForm.handleSubmit((data) =>
                onSubmit(
                  data,
                  relatedMatterOptions.map((option) => option.id),
                  true,
                ),
              )();
              return;
            }
            setCloseAfterSave(true);
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      <Form {...incidentReportForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          titleTrailing={
            editing && incidentReport ? (
              <LinkedMatterMembershipPopover
                canUnlink={canDelete}
                currentMatterId={matter.id}
                enabled={open}
                objectId={incidentReport.id}
                objectType="incident-report"
                onUnlink={() => {
                  setIsUnlinking(true);
                }}
              />
            ) : undefined
          }
          description={description}
          size="3xl"
          className="h-[min(80vh,780px)] w-[calc(100vw-2rem)] max-w-7xl"
          loading={isFormLoading}
          isDirty={incidentReportForm.formState.isDirty}
          footer={modalFooter}
          previewLabel={null}
          preview={
            <fieldset
              className="min-w-0 border-0 p-0"
              disabled={fieldsetDisabled}
            >
              <AddContactFormField
                {...incidentReportModalContactPickerProps}
                label="Source"
                register={incidentReportForm.register(
                  "incidentReportSourceContacts",
                )}
                onDelete={(contact) => {
                  incidentReportForm.setValue(
                    "incidentReportSourceContacts",
                    incidentReportForm
                      .getValues("incidentReportSourceContacts")
                      .filter((p) => getSelectedContactId(p) !== contact.id),
                    { shouldDirty: true },
                  );
                }}
                allowMultiple
                matterId={matter.id}
                defaultSearchField="name"
                parentSlideoverOpen={open}
                renderSelectedContactPreview={(contact) =>
                  incidentReportSourceMatterContactRolePreview(
                    contact,
                    matter.id,
                    incidentReportSourceConnections.find(
                      (c) => c.targetObjectId === contact.id,
                    ),
                    () => {
                      incidentReportForm.setValue(
                        "incidentReportSourceContacts",
                        incidentReportForm
                          .getValues("incidentReportSourceContacts")
                          .filter(
                            (p) => getSelectedContactId(p) !== contact.id,
                          ),
                        { shouldDirty: true },
                      );
                    },
                  )
                }
              />
            </fieldset>
          }
          bodyFormId={INCIDENT_REPORT_MODAL_FORM_ID}
          bodyFormOnSubmit={submitIncidentReportForm}
        >
          {incidentReportMainFieldsFieldset}
        </AppModal>
      </Form>

      {editing && incidentReport && (
        <RelatedMatterObjectDeleteModal
          open={isDeleting}
          onOpenChange={setIsDeleting}
          currentMatterId={matter.id}
          objectId={incidentReport.id}
          objectLabel="Incident report"
          objectType="incident-report"
          onCurrentMatterRemoved={() => {
            setOpen(false);
          }}
        />
      )}
      {editing && incidentReport && (
        <ActionConfirmationModal
          open={isUnlinking}
          setOpen={setIsUnlinking}
          title="Unlink this incident report from the matter?"
          description="This removes the incident report from this matter only. If this is its last matter, the incident report will be deleted."
          buttonText="Unlink incident report"
          afterActionFn={async () => {
            await unlinkIncidentReport.mutateAsync({
              matterId: matter.id,
              objectId: incidentReport.id,
              objectType: "incident-report",
            });
            setOpen(false);
          }}
          isActionPending={unlinkIncidentReport.isPending}
        />
      )}
    </>
  );
};
